import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, RefreshCw, Search, ExternalLink } from 'lucide-react';

const RESOLUTION_LABELS = {
  mapped_existing: 'Matched',
  excluded:        'Excluded',
  new_product:     'New Product',
  cross_site_alias:'Cross-site',
};

function ResolutionBadge({ type }) {
  const label = RESOLUTION_LABELS[type] || type;
  const colour =
    type === 'mapped_existing'  ? 'bg-emerald-100 text-emerald-800' :
    type === 'excluded'         ? 'bg-red-100 text-red-700' :
    type === 'cross_site_alias' ? 'bg-purple-100 text-purple-800' :
                                  'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[11px] font-medium ${colour}`}>
      {label}
    </span>
  );
}

export default function PsbAliasRegistryPage() {
  const navigate = useNavigate();

  const [rows,      setRows]      = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [pageSize]                = useState(50);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  // Filters
  const [qHouse,    setQHouse]    = useState('');
  const [qCode,     setQCode]     = useState('');
  const [qRes,      setQRes]      = useState('');

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: pg, page_size: pageSize });
      if (qHouse.trim()) params.set('house_id',   qHouse.trim());
      if (qCode.trim())  params.set('item_code',  qCode.trim());
      if (qRes.trim())   params.set('resolution', qRes.trim());
      const res = await api.get(`/admin/psb-stage1b/alias-registry?${params.toString()}`);
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setPage(pg);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load alias registry.');
    } finally {
      setLoading(false);
    }
  }, [qHouse, qCode, qRes, pageSize]);

  useEffect(() => { load(1); }, []); // eslint-disable-line

  const pages = Math.ceil(total / pageSize) || 1;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-sm"
            data-testid="alias-registry-back"
          >
            <ChevronLeft size={16} /> Back
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Alias Registry</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Confirmed source-to-master product identity records for your organisation.
              Each row records a human-approved (or cross-site learned) mapping — these
              are the evidence base for cross-site auto-matching.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-1">
            <Search size={14} className="text-slate-400" />
            <Input
              className="h-8 text-xs w-36"
              placeholder="PS house ID"
              value={qHouse}
              onChange={e => setQHouse(e.target.value)}
              data-testid="alias-filter-house"
            />
          </div>
          <Input
            className="h-8 text-xs w-32"
            placeholder="Item code"
            value={qCode}
            onChange={e => setQCode(e.target.value)}
            data-testid="alias-filter-code"
          />
          <select
            className="h-8 text-xs border border-input rounded px-2 bg-background"
            value={qRes}
            onChange={e => setQRes(e.target.value)}
            data-testid="alias-filter-resolution"
          >
            <option value="">All resolutions</option>
            <option value="mapped_existing">Matched</option>
            <option value="excluded">Excluded</option>
            <option value="new_product">New Product</option>
            <option value="cross_site_alias">Cross-site</option>
          </select>
          <Button
            size="sm" className="h-8 text-xs"
            onClick={() => load(1)}
            data-testid="alias-filter-apply"
          >
            <Search size={12} className="mr-1" /> Search
          </Button>
          <Button
            size="sm" variant="outline" className="h-8 text-xs"
            onClick={() => { setQHouse(''); setQCode(''); setQRes(''); setTimeout(() => load(1), 0); }}
            data-testid="alias-filter-clear"
          >
            Clear
          </Button>
        </div>

        {/* Summary bar */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-slate-500">
            {total} record{total !== 1 ? 's' : ''} total
            {total > pageSize && ` — page ${page} of ${pages}`}
          </span>
          <button
            onClick={() => load(page)}
            className="text-xs text-slate-400 hover:text-slate-700 flex items-center gap-1"
            data-testid="alias-refresh"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="alias-error">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-3 py-2 text-left font-semibold text-slate-600 w-10">#</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Site</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Item Code</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Source Product</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Resolution</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">AZ Product</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Status</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Confirmed by</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Notes</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-slate-400">Loading…</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-slate-400">
                    No alias registry records found.
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <tr
                  key={r.id}
                  className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  data-testid={`alias-row-${r.id}`}
                >
                  <td className="px-3 py-2 text-slate-400">{(page - 1) * pageSize + i + 1}</td>
                  <td className="px-3 py-2 text-slate-700">
                    <div className="font-medium">{r.site_name || r.legacy_house_id}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{r.legacy_house_id}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-700">{r.legacy_item_code}</td>
                  <td className="px-3 py-2 text-slate-700 max-w-[180px]">
                    <div className="font-medium truncate" title={r.source_description}>
                      {r.source_description || '—'}
                    </div>
                    <div
                      className="font-mono text-[10px] text-slate-400 break-all"
                      title={r.source_sig_hash}
                    >
                      {r.source_sig_hash?.slice(0, 10)}…
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <ResolutionBadge type={r.resolution_type} />
                  </td>
                  <td className="px-3 py-2">
                    {r.az_product_id ? (
                      <span className="text-slate-800">
                        {r.az_product_name || '—'}
                        <span className="text-slate-400 ml-1">#{r.az_product_id}</span>
                      </span>
                    ) : (
                      <span className="text-slate-400 italic">
                        {r.exclusion_reason ? `Excluded: ${r.exclusion_reason.slice(0, 50)}` : '—'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[11px] font-medium ${
                      r.revalidation_status === 'valid'    ? 'bg-emerald-100 text-emerald-800' :
                      r.revalidation_status === 'reverted' ? 'bg-red-100 text-red-700' :
                                                             'bg-slate-100 text-slate-600'
                    }`}>
                      {r.revalidation_status || '—'}
                    </span>
                    {r.admin_overridden && (
                      <span className="ml-1 inline-flex px-1.5 py-0.5 rounded text-[11px] bg-amber-100 text-amber-700">
                        override
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                    {r.approved_at
                      ? new Date(r.approved_at).toLocaleDateString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })
                      : '—'}
                    {r.confirmed_by_email && (
                      <div className="text-[10px] text-slate-400 truncate max-w-[150px]" title={r.confirmed_by_email}>
                        {r.confirmed_by_email}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500 max-w-xs truncate" title={r.notes}>
                    {r.notes || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button
              size="sm" variant="outline" className="h-7 text-xs"
              disabled={page <= 1 || loading}
              onClick={() => load(page - 1)}
              data-testid="alias-prev-page"
            >
              Previous
            </Button>
            <span className="text-xs text-slate-500">Page {page} of {pages}</span>
            <Button
              size="sm" variant="outline" className="h-7 text-xs"
              disabled={page >= pages || loading}
              onClick={() => load(page + 1)}
              data-testid="alias-next-page"
            >
              Next
            </Button>
          </div>
        )}

      </div>
    </div>
  );
}
